import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography,
} from '@mui/material';
import api from '../../services/api';
import { COR, extrairErros, formatarData, formatarReal } from './financeiroUtils';

// Altera o valor total de um CONTRATO NOVO (aditivo ou correção) e guarda o histórico.
// Para projetos antigos continua valendo o EditarContratoDialog de sempre.
export default function EditarValorContratoDialog({ contrato, onFechar, onSalvo }) {
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!contrato) return;
    setValor(contrato.valor_contrato ?? '');
    setMotivo('');
    setErro('');
  }, [contrato]);

  const salvar = async () => {
    if (!valor || Number(valor) <= 0) { setErro('Informe um valor maior que zero.'); return; }
    if (Number(valor) === Number(contrato.valor_contrato)) { setErro('O valor é o mesmo de antes. Altere o valor ou feche a janela.'); return; }
    setSalvando(true);
    setErro('');
    try {
      await api.patch(`/contratos/${contrato.id}/`, { valor_contrato: valor, motivo_valor_contrato: motivo.trim() });
      await onSalvo();
    } catch (err) {
      const e = extrairErros(err, 'Não foi possível salvar o valor do contrato.');
      setErro(e.campos.valor_contrato || e.mensagem);
    } finally {
      setSalvando(false);
    }
  };

  const historico = contrato?.historico_valor || [];

  return (
    <Dialog open={Boolean(contrato)} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { '& .MuiButton-root': { textTransform: 'none', fontWeight: 600 } } } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography component="span" sx={{ display: 'block', fontSize: 20, fontWeight: 700, color: COR.ink }}>Alterar valor do contrato</Typography>
        <Typography component="span" sx={{ display: 'block', fontSize: 14, fontWeight: 400, color: COR.texto2 }}>
          {contrato?.nome}{contrato?.cliente_nome ? `, ${contrato.cliente_nome}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'grid', gap: 2 }}>
          {erro && <Alert severity="error">{erro}</Alert>}
          <Typography sx={{ fontSize: 14, color: COR.texto2 }}>
            Valor atual: <b style={{ color: COR.ink }}>{contrato?.valor_contrato != null ? formatarReal(contrato.valor_contrato) : 'não informado'}</b>
          </Typography>
          <TextField
            label="Novo valor total do contrato" type="number" size="small" fullWidth value={valor}
            onChange={(e) => setValor(e.target.value)}
            slotProps={{ input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }}
          />
          <TextField
            label="Motivo (ex.: aditivo, correção)" size="small" fullWidth value={motivo}
            onChange={(e) => setMotivo(e.target.value)} slotProps={{ htmlInput: { maxLength: 300 } }}
          />
          {historico.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: COR.ink, mb: 0.5 }}>Histórico do valor</Typography>
              {historico.map((h) => (
                <Typography key={h.id} sx={{ fontSize: 13, color: COR.texto2, fontVariantNumeric: 'tabular-nums' }}>
                  {formatarData(h.criado_em)}: {h.valor_anterior != null ? `${formatarReal(h.valor_anterior)} para ` : ''}{formatarReal(h.valor_novo)}
                  {h.motivo ? `, ${h.motivo}` : ''}{h.alterado_por_nome ? ` (${h.alterado_por_nome})` : ''}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFechar} disabled={salvando} sx={{ color: COR.texto2 }}>Cancelar</Button>
        <Button variant="contained" onClick={salvar} disabled={salvando} sx={{ backgroundColor: COR.navy, '&:hover': { backgroundColor: COR.ink } }}>
          {salvando ? 'Salvando...' : 'Salvar valor'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
